import { zodResolver } from "@hookform/resolvers/zod";
import { Folder } from "certiblok-api-manager";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { AvailableFolderColors } from "../../../constants/AvailableFolderColors";
import { useFetchFolderData } from "./useFetchFolderData";
import { EditCreateFolderFormValues, useEditCreateFolderZodSchema } from "./useEditCreateFolderZodSchema";

export const useInitEditCreateFolderForm = ({
	folder,
	parentFolderId,
	isEditRoom,
}: {
	folder?: Folder;
	parentFolderId?: string;
	isEditRoom?: boolean;
}) => {
	const { data, isLoading } = useFetchFolderData({
		folder,
		parentFolderId,
		isEditRoom,
	});

	const editCreateFolderZodSchema = useEditCreateFolderZodSchema();

	const form = useForm<EditCreateFolderFormValues>({
		resolver: zodResolver(editCreateFolderZodSchema),
		defaultValues: {
			name: data?.folderData?.name ?? "",
			color: data?.folderData?.color ?? AvailableFolderColors[0],
			isReserved: data?.folderData?.isReserved ?? false,
			giveAccessToSubfolders: false,
			reservedToUsers:
				data?.folderData?.reservedToUsers?.map((user) => ({
					...user,
					_id: user._id ?? user.id,
				})) ??
				data?.parentFolder?.reservedToUsers?.map((user) => ({
					...user,
					_id: user._id ?? user.id,
				})) ??
				[],
			reservedToTeams: data?.folderData?.reservedToTeams ?? data?.parentFolder?.reservedToTeams ?? [],
		},
	});

	useEffect(() => {
		form.reset({
			name: data?.folderData?.name ?? "",
			color: data?.folderData?.color ?? AvailableFolderColors[0],
			isReserved: data?.folderData?.isReserved ?? false,
			giveAccessToSubfolders: false,
			reservedToUsers:
				data?.folderData?.reservedToUsers?.map((user) => ({
					...user,
					_id: user._id ?? user.id,
				})) ??
				data?.parentFolder?.reservedToUsers?.map((user) => ({
					...user,
					_id: user._id ?? user.id,
				})) ??
				[],
			reservedToTeams: data?.folderData?.reservedToTeams ?? data?.parentFolder?.reservedToTeams ?? [],
		});
	}, [data, form]);

	return {
		form,
		isLoading,
		parentFolder: data?.parentFolder,
		isParentRestricted: (data?.parentFolder?.reservedToUsers ?? []).length > 0,
	};
};
